<div
  class="import-export-panel position-absolute p-2 rounded-2 start-5 z-3 top-26 max-vh-70 min-vh-55 min-wpx-350"
  cdkDrag
  cdkDragBoundary="app-root"
  [class.d-none]="!isVisible"
>
  <div class="d-flex justify-content-between align-items-center gap-2 flex-shrink-0">
    <span
      cdkDragHandle
      class="import-export-drag-handle rb-ic rb-ic-drag-handle fs-2xl cursor-pointer hover-effect me-1 flex-shrink-0"
    ></span>
    <button
      class="import-export-action w-100 btn btn-hover mw-100 position-relative"
      [disabled]="
        !editorService.getValidWarnings().length ||
        editorService.getCorruptedWarnings().length > 0 ||
        isPushLoading()
      "
      (click)="pushWarnings($event)"
      ngbTooltip="Push local warnings as a batch"
      container="body"
      placement="top"
      [openDelay]="300"
    >
      <span [class.invisible]="isPushLoading()">Push</span>
      <div class="position-absolute translate-middle start-50 top-50">
        <div
          class="import-export-spinner spinner-border spinner-border-sm"
          [class.d-none]="!isPushLoading()"
          role="status"
        ></div>
      </div>
    </button>
    <button
      class="import-export-action w-100 btn btn-hover mw-100"
      (click)="importFromFile()"
      ngbTooltip="Import warnings from a JSON file"
      container="body"
      placement="top"
      [openDelay]="300"
    >
      Import
    </button>
    @if (legacyImportCompatibilityEnabled) {
      <button
        class="import-export-action w-100 btn btn-hover mw-100"
        (click)="openLegacyImportForm()"
        ngbTooltip="Import legacy warning payloads (deprecated in the future)"
        container="body"
        placement="top"
        [openDelay]="300"
      >
        Legacy
      </button>
    }
    <a
      class="import-export-action w-100 btn btn-hover mw-100"
      (click)="exportToFile()"
      ngbTooltip="Export all local warnings to a JSON file"
      container="body"
      placement="top"
      [openDelay]="300"
      >Export</a
    >
    <div class="d-flex align-items-center gap-2">
      <button
        type="button"
        class="import-export-help-button"
        aria-label="Open simulation editor help"
        (click)="helpRequested.emit()"
        ngbTooltip="Open editor help"
        container="body"
        placement="top"
        [openDelay]="300"
      >
        i
      </button>
      <span
        class="justify-content-center rb-ic rb-ic-close fs-2xl p-1 cursor-pointer hover-effect"
        (click)="close()"
      ></span>
    </div>
  </div>

  <input #fileInput type="file" accept=".json" class="d-none" (change)="onFileSelected($event)" />

  @if (showLegacyImportForm) {
    <div class="import-export-legacy-form mt-2 p-2 border rounded-2">
      <div class="fw-semibold mb-2">Legacy warning import</div>
      <textarea
        class="form-control mb-2"
        rows="10"
        [(ngModel)]="legacyImportText"
        placeholder="Paste the old warning JSON here"
      ></textarea>
      @if (legacyImportFailures.length > 0) {
        <div
          class="mt-2 p-2 border border-warning rounded-2 bg-warning-subtle text-warning-emphasis small"
        >
          <div class="fw-semibold mb-1">Import skipped</div>
          <ul class="mb-0 ps-3">
            @for (failure of legacyImportFailures; track failure.id) {
              <li>
                {{ failure.reason }}:
                {{
                  failure.type === 'segment'
                    ? 'segmentId=' + failure.id
                    : failure.type === 'area'
                      ? 'h3id=' + failure.id
                      : failure.id
                }}
              </li>
            }
          </ul>
        </div>
      }
      <div class="d-flex gap-2 mt-2">
        <button class="btn import-export-action w-100" (click)="saveLegacyImport()">Save</button>
        <button class="btn import-export-action w-100" (click)="closeLegacyImportForm()">
          Cancel
        </button>
      </div>
    </div>
  }

  <div class="import-export-list mt-2 p-1">
    @if (((editorService.corruptedWarnings$ | async)?.length ?? 0) > 0) {
      <div class="import-export-list-section-title import-export-list-section-title--corrupted">
        Corrupted ({{ (editorService.corruptedWarnings$ | async)?.length }})
      </div>
      @for (w of editorService.corruptedWarnings$ | async; track w.properties.featureId) {
        <button
          #warningListItem
          [attr.data-feature-id]="w.properties.featureId"
          class="import-export-list-item import-export-list-item-corrupted btn w-100 text-start mb-1"
          [class.import-export-list-item-selected]="isWarningSelected(w.properties.featureId)"
          (click)="selectWarning(w)"
        >
          <div
            class="import-export-list-item-content d-flex align-items-center justify-content-between gap-2"
          >
            <div>
              <div class="fw-semibold">
                {{ getMetricForWarning(w)?.name ?? w.properties.warningName }}
              </div>
              <div class="import-export-list-item-meta">{{ getWarningIdentifier(w) }}</div>
            </div>
            <div class="import-export-list-item-badge">
              <img
                class="import-export-list-item-icon"
                [src]="getWarningImagePath(w.properties.warningName)"
                [alt]="w.properties.warningName"
              />
              <span class="import-export-list-item-corruption-marker">?</span>
            </div>
          </div>
        </button>
      }
    }

    @for (w of editorService.validWarnings$ | async; track w.properties.featureId) {
      <button
        #warningListItem
        [attr.data-feature-id]="w.properties.featureId"
        class="import-export-list-item btn w-100 text-start mb-1"
        [class.import-export-list-item-selected]="isWarningSelected(w.properties.featureId)"
        (click)="selectWarning(w)"
      >
        <div
          class="import-export-list-item-content d-flex align-items-center justify-content-between gap-2"
        >
          <div>
            <div class="fw-semibold">
              {{ getMetricForWarning(w)?.name ?? w.properties.warningName }}
            </div>
            <div class="import-export-list-item-meta">{{ getWarningIdentifier(w) }}</div>
          </div>
          <div class="import-export-list-item-badge">
            <img
              class="import-export-list-item-icon"
              [src]="getWarningImagePath(w.properties.warningName)"
              [alt]="w.properties.warningName"
            />
          </div>
        </div>
      </button>
    }

    @if (
      editorService.getValidWarnings().length === 0 &&
      editorService.getCorruptedWarnings().length === 0
    ) {
      <div class="import-export-caption p-2">
        Import a JSON file or create warnings while this panel is open.
      </div>
    }
  </div>

  @if (selectedWarning(); as selectedWarning) {
    <div class="import-export-editor mt-2 p-2">
      <div class="import-export-editor-title mb-2">Warning Properties</div>
      <div class="import-export-editor-fields">
        <div class="mb-2">
          <label class="form-label mb-1">TTL (s)</label>
          <input
            class="form-control"
            type="number"
            min="1"
            step="1"
            [(ngModel)]="selectedWarningTtlSeconds"
          />
        </div>
        <ng-container *hasPermissionAny="[Permissions.RCS_Apps_Simulation_Extended_Access]">
          <div class="mb-2">
            <label class="form-label mb-1">Confidence</label>
            <input
              class="form-control"
              type="number"
              min="0"
              max="1"
              step="0.01"
              [(ngModel)]="selectedWarningConfidence"
            />
          </div>
          <div class="mb-2">
            <label class="form-label mb-1">DFRS Classification</label>
            <select class="form-select" [(ngModel)]="selectedWarningDfrsClassification">
              <option value="DFRS_NONE">DFRS_NONE</option>
              <option value="DFRS_PARTLY">DFRS_PARTLY</option>
              <option value="DFRS_ONLY">DFRS_ONLY</option>
            </select>
          </div>
        </ng-container>
        <div class="mb-2">
          <label class="form-label mb-1">Targets</label>
          @if (getAvailableScopes().length > 0) {
            <div class="import-export-targets-list">
              @for (scope of getAvailableScopes(); track scope) {
                <label class="import-export-target-item">
                  <input
                    type="checkbox"
                    class="import-export-target-checkbox"
                    [checked]="isTargetSelected(scope)"
                    (change)="toggleTargetSelection(scope)"
                  />
                  <span class="import-export-target-text">{{ scope }}</span>
                </label>
              }
            </div>
          } @else {
            <div class="import-export-target-empty">No scopes available</div>
          }
        </div>
      </div>
      <div class="d-flex gap-2 mt-3 flex-shrink-0">
        <button
          class="btn import-export-action w-100"
          (click)="saveSelectedWarning()"
          [disabled]="selectedWarningTargets.length === 0 || isSelectedWarningInvalid()"
        >
          Save
        </button>
        <button class="btn import-export-action w-100" (click)="deselectWarning()">Close</button>
        <button class="btn btn-dangerous text-lightgray w-100" (click)="removeSelectedWarning()">
          Remove
        </button>
      </div>
    </div>
  }
</div>
